import axios from './axios';
import {realtimeClient} from './workspaceRealtime';

const unwrap = (response) => response?.data?.data ?? response?.data ?? response;

function compact(input, fields) {
  return fields.reduce((result, field) => {
    if (input?.[field] !== undefined && input[field] !== '') result[field] = input[field];
    return result;
  }, {});
}

function normalizeMedia(media) {
  if (!media) return null;
  return {...media, byteSize: Math.max(0, Number(media.byteSize) || 0)};
}

function normalizeMessage(message = {}) {
  return {
    ...message,
    retryCount: Math.max(0, Number(message.retryCount) || 0),
    media: normalizeMedia(message.media),
  };
}

function normalizeConversation(conversation = {}) {
  return {
    ...conversation,
    connectionStatus: conversation.connectionStatus || 'disconnected',
    profilePictureUrl: conversation.profilePictureUrl || null,
    unreadCount: Math.max(0, Number(conversation.unreadCount) || 0),
  };
}

export async function fetchWhatsappConversations(filters) {
  const response = unwrap(await axios.get('/api/whatsapp/conversations', {
    params: compact(filters, ['workspaceId', 'connectionId', 'filter', 'query', 'campaignId', 'cursor', 'limit']),
    cache: false,
    signal: filters?.signal,
  })) || {};
  return {
    items: (response.items || []).map(normalizeConversation),
    nextCursor: response.nextCursor || null,
    unreadTotal: Math.max(0, Number(response.unreadTotal) || 0),
  };
}

export async function fetchWhatsappLeadCandidates(filters) {
  const response = unwrap(await axios.get('/api/whatsapp/leads', {
    params: compact(filters, ['workspaceId', 'query', 'limit']),
    cache: false,
  }));
  return response || [];
}

export async function createWhatsappConversation(input) {
  return unwrap(await axios.post('/api/whatsapp/conversations', compact(input, ['workspaceId', 'leadId', 'connectionId'])));
}

export async function fetchWhatsappMessages(conversationId, filters) {
  const response = unwrap(await axios.get(`/api/whatsapp/conversations/${conversationId}/messages`, {
    params: compact(filters, ['workspaceId', 'cursor', 'limit']),
    cache: false,
    signal: filters?.signal,
  })) || {};
  return {
    items: (response.items || []).map(normalizeMessage),
    nextCursor: response.nextCursor || null,
  };
}

export async function markWhatsappConversationRead(conversationId, workspaceId) {
  return unwrap(await axios.post(`/api/whatsapp/conversations/${conversationId}/read`, {workspaceId}));
}

export async function sendWhatsappMessage(conversationId, input) {
  const payload = compact(input, ['workspaceId', 'body', 'caption', 'mediaId', 'idempotencyKey', 'purpose']);
  if (input?.messageType === 'voice') payload.messageType = 'voice';
  return normalizeMessage(unwrap(await axios.post(`/api/whatsapp/conversations/${conversationId}/messages`, payload)));
}

export async function retryWhatsappMessage(messageId, workspaceId) {
  return normalizeMessage(unwrap(await axios.post(`/api/whatsapp/messages/${messageId}/retry`, {workspaceId})));
}

export async function prepareWhatsappMedia(input) {
  return unwrap(await axios.post('/api/whatsapp/media', compact(input, [
    'workspaceId',
    'conversationId',
    'mimeType',
    'filename',
    'byteSize',
  ])));
}

export async function completeWhatsappMedia(mediaId, input) {
  return normalizeMedia(unwrap(await axios.post(`/api/whatsapp/media/${mediaId}/complete`, {
    workspaceId: input.workspaceId,
  })));
}

export async function fetchWhatsappMediaUrl(mediaId, workspaceId) {
  return unwrap(await axios.get(`/api/whatsapp/media/${mediaId}`, {
    params: {workspaceId},
    cache: false,
  }));
}

export async function uploadWhatsappMedia(uploadPath, uploadToken, file, signal) {
  if (signal?.aborted) throw new DOMException('The operation was aborted', 'AbortError');
  const {data, error} = await realtimeClient.storage
    .from('whatsapp-media')
    .uploadToSignedUrl(uploadPath, uploadToken, file, {contentType: file.type});
  if (signal?.aborted) throw new DOMException('The operation was aborted', 'AbortError');
  if (error) throw error;
  return data;
}

export async function fetchWhatsappCampaignEligibility(campaignId, workspaceId) {
  return unwrap(await axios.get(`/api/whatsapp/campaigns/${campaignId}/eligibility`, {params: {workspaceId}, cache: false}));
}
